import { useQuery } from "@connectrpc/connect-query";
import { Link, createFileRoute } from "@tanstack/react-router";

import { formatNumber } from "@yatayat/core/money";
import { NetworkService } from "@yatayat/gen/yatayat/network/v1/network_pb";

import { HillsArt } from "~/components/HillsArt";
import { useI18n, type MessageKey } from "~/lib/i18n";
import { runsAsApp, useInstall } from "~/lib/install";
import { ui } from "~/lib/ui";

export const Route = createFileRoute("/")({
  component: CitizenHome,
});

const features: { title: MessageKey; body: MessageKey; to: "/fare-check" | "/book" | "/v" | "/complaints" }[] = [
  { title: "fareCheck", body: "fareCheckBody", to: "/fare-check" },
  { title: "bookSeat", body: "bookSeatBody", to: "/book" },
  { title: "verifyVehicle", body: "verifyVehicleBody", to: "/v" },
  { title: "reportProblem", body: "complaintsBody", to: "/complaints" },
];

function CitizenHome() {
  const { t } = useI18n();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-semibold text-ink dark:text-ink-dark">{t("appName")}</h1>
        <p className={ui.lead}>{t("tagline")}</p>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{t("systemName")}</p>
      </div>
      <HillsArt className="h-20 sm:h-28" />
      <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {features.map((f) => (
          <li key={f.title} className="rounded-xl border border-slate-200 p-4 dark:border-slate-800">
            <h2 className="font-semibold">{t(f.title)}</h2>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{t(f.body)}</p>
            <Link
              to={f.to}
              className={`mt-3 inline-block ${ui.primary}`}
            >
              {t("open")}
            </Link>
          </li>
        ))}
      </ul>
      <p className="text-sm">
        <Link to="/open-data" className="text-ink underline dark:text-ink-dark">
          {t("openDataLink")}
        </Link>
      </p>
      <InstallLink />
      <ApiStatus />
    </div>
  );
}

/** Points browsers at the install page; nothing once the site runs as an app. */
function InstallLink() {
  const { t } = useI18n();
  const { state } = useInstall();

  if (runsAsApp(state)) return null;

  return (
    <p className="text-sm">
      <Link to="/app" className="text-ink underline dark:text-ink-dark">
        {t("installLink")}
      </Link>
    </p>
  );
}

/** Proves the round trip from the browser through Connect to Postgres. */
function ApiStatus() {
  const { t, lang } = useI18n();
  const { data, isError, isPending } = useQuery(NetworkService.method.listPlaces, { pageSize: 100 });

  if (isPending) return null;

  return (
    <p className={`text-sm ${isError ? "text-brand dark:text-brand-dark" : "text-emerald-700 dark:text-emerald-400"}`}>
      {isError ? t("apiDown") : `${t("apiConnected")} · ${t("apiPlaces", { n: formatNumber(data.places.length, lang) })}`}
    </p>
  );
}
